import { api, formatError } from '../api-client'
import { isGitRepo } from '../git'
import { resolveRepo, notLinkedMessage } from '../repo-resolver'
import { analyticsCommandError, analyticsEvent, flushAndExit } from '../analytics'

interface RepoRow {
  id: string
  github_org: string
  github_repo: string
  default_branch: string | null
  tracked_branches: string[] | null
}

function emitJSON(data: unknown): void {
  console.log(JSON.stringify(data, null, 2))
}

// `driftless branches` — view/set which branches' pushes count as drift.
// The default branch is always tracked (auto, zero-config); this is the
// escape hatch to add a release/integration branch when GitHub's default
// branch isn't the team's real source of truth.
export async function branchesCommand(args: string[]): Promise<void> {
  if (!isGitRepo()) {
    console.error('Error: not a git repository.')
    process.exit(1)
  }

  const sub = args[0] && !args[0].startsWith('--') ? args[0] : undefined
  const name = args[1] && !args[1].startsWith('--') ? args[1] : undefined
  const isJSON = args.includes('--json')

  if (sub && sub !== 'add' && sub !== 'rm') {
    console.error(`Unknown subcommand '${sub}'. Usage: driftless branches [add|rm] <branch>`)
    process.exit(1)
  }

  const resolution = await resolveRepo()
  if (!resolution.ok) {
    if (resolution.reason === 'no_remote') {
      console.error('Error: no git remote configured.')
    } else if (resolution.reason === 'not_linked') {
      console.error(notLinkedMessage(resolution.remote!, resolution.workspaceSlug!))
    } else {
      // Surface the same diagnostic sync prints — distinguishes a transient
      // API failure (last endpoint rejected) from a genuine config problem,
      // instead of a bare "run doctor".
      console.error('Error: could not resolve workspace.')
      if (resolution.detail) console.error(resolution.detail)
      console.error('  If the last endpoint was rejected, this is likely transient — retry.')
    }
    await analyticsCommandError('branches', resolution.workspaceSlug, resolution.reason)
    await flushAndExit(1)
    return
  }

  const { workspaceSlug, repoId, remote } = resolution

  let repo: RepoRow | undefined
  try {
    const repos = (await api.get(`/workspaces/${workspaceSlug}/repos`)) as RepoRow[]
    repo = repos.find((r) => r.id === repoId)
  } catch (e: any) {
    console.error(`Failed to load repo: ${formatError(e)}`)
    await analyticsCommandError('branches', workspaceSlug, formatError(e))
    await flushAndExit(1)
    return
  }
  if (!repo) {
    console.error('Repo not found in workspace. Run `driftless doctor` to diagnose, or create topics without linking.')
    process.exit(1)
  }

  const defaultBranch = repo.default_branch
  let tracked = repo.tracked_branches ?? []

  if (sub === 'add' || sub === 'rm') {
    if (!name) {
      console.error(`Usage: driftless branches ${sub} <branch>`)
      process.exit(1)
    }
    if (sub === 'add') {
      if (name === defaultBranch) {
        console.log(`'${name}' is the default branch — already tracked automatically.`)
        analyticsEvent('cli_branches', workspaceSlug, { action: 'set' })
        await flushAndExit(0)
      }
      tracked = Array.from(new Set([...tracked, name]))
    } else {
      tracked = tracked.filter((b) => b !== name)
    }
    try {
      const updated = (await api.patch(
        `/workspaces/${workspaceSlug}/repos/${repoId}`,
        { tracked_branches: tracked },
      )) as RepoRow
      tracked = updated.tracked_branches ?? []
    } catch (e: any) {
      console.error(`Update failed: ${formatError(e)}`)
      await analyticsCommandError('branches', workspaceSlug, formatError(e))
      await flushAndExit(1)
      return
    }
  }

  analyticsEvent('cli_branches', workspaceSlug, {
    action: sub === 'add' || sub === 'rm' ? 'set' : 'list',
  })

  if (isJSON) {
    emitJSON({
      repo: `${remote.org}/${remote.repo}`,
      default_branch: defaultBranch,
      tracked_branches: tracked,
      effective: [defaultBranch, ...tracked].filter(Boolean),
    })
    await flushAndExit(0)
  }

  console.log(`▌ ${remote.org}/${remote.repo}`)
  console.log(`  drift is detected only from pushes to:\n`)
  console.log(`  ${defaultBranch ?? '(default branch — resolved on first push)'}   ← default branch · auto · always tracked`)
  for (const b of tracked) console.log(`  ${b}   ← extra · you added this`)
  if (tracked.length === 0) {
    console.log(`\n  Only the default branch is tracked (the zero-config norm).`)
    console.log(`  Add a release/integration branch:  driftless branches add <name>`)
  } else {
    console.log(`\n  Remove an extra branch:  driftless branches rm <name>`)
  }
  await flushAndExit(0)
}
